import type { AlertColor } from '@mui/material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  createTheme,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Slider,
  Snackbar,
  Stack,
  TextField,
  ThemeProvider,
  Typography,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import {
  Activity,
  ArrowLeft as ArrowBack,
  ArrowRight as ArrowForward,
  Check,
  Heart,
} from "lucide-react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  chip,
  chipSelected,
  chipUnselected,
  completeText,
  container,
  formLabel,
  formPaper,
  header,
  headerTitle,
  navButton,
  navigationButtons,
  progressBar,
  progressContainer,
  questionCard,
  questionCardActive,
  questionHeader,
  questionIndicator,
  questionIndicatorsContainer,
  questionTitle,
  radioGroup,
  radioOption,
  submitButton,
} from "./HealthAssessmentStyle";
import { useGetQuestionsQuery } from 'services/questionApi';
import { useCreateHealthAssessmentMutation } from 'services/healthAssessmentApi';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { StorageKeys } from 'utils/storage';

const theme = createTheme({
  palette: {
    primary: {
      main: "#150c56",
      light: "#433c88",
      dark: "#0e0839",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#6537ef",
      light: "#835ef2",
      dark: "#4b27b1",
      contrastText: "#ffffff",
    },
  },
});

// FIXED: Use exact type from RTK Query (no local interface needed)
type Question = NonNullable<ReturnType<typeof useGetQuestionsQuery>['data']>['data'][0];

interface HealthFormData {
  [key: string]: string | number | null;
}

// Helper to parse options
const parseOptions = (options: string | null): string[] => {
  if (!options) return [];
  try {
    return JSON.parse(options);
  } catch {
    return options.split(',').map(opt => opt.trim());
  }
};

export default function HealthQuestionnaire() {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const questionsPerPage = 5;

  const { data: questionsResponse, isLoading, isError } = useGetQuestionsQuery({
    limit: 100,
    sortBy: "order_index",
    sortOrder: "ASC",
  });

  const [createHealthAssessment] = useCreateHealthAssessmentMutation();

  // FIXED: Safely extract the actual array of questions
  const rawData = questionsResponse?.data;
  const healthQuestions: Question[] = Array.isArray(rawData)
    ? rawData
    : (rawData as any)?.data || [];

  const totalPages = Math.ceil(healthQuestions.length / questionsPerPage);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<HealthFormData>({
    defaultValues: {},
  });

  const formValues = watch();

  const getCurrentQuestions = (): Question[] => {
    const start = currentPage * questionsPerPage;
    const end = start + questionsPerPage;
    return healthQuestions.slice(start, end);
  };

  const handleNext = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const showNotification = (message: string, severity: AlertColor = 'success') => {
    setNotification({ open: true, message, severity });
  };

  const onSubmit = async (formData: HealthFormData) => {
    try {
      setIsSubmitting(true);

      const answers = Object.entries(formData)
        .filter(([_, v]) => v !== null && v !== '' && v !== undefined)
        .map(([question_id, answer]) => ({
          question_id,
          answer_boolean: answer === 'yes' ? true : answer === 'no' ? false : answer,
        }));

      const assessmentData = {
        patient_id: localStorage.getItem(StorageKeys.PATIENT_ID),
        answers,
        assessment_date: new Date().toISOString(),
      };

      // FIXED: .unwrap() returns the actual data, not { data: ... }
      const result = await createHealthAssessment(assessmentData).unwrap() as any;

      if (result?.data?.id) {
        localStorage.setItem('health_assessment_id', result.data.id);
        setTimeout(() => navigate('/visual-health'), 800);
      }
      showNotification('Health assessment submitted successfully!', 'success');
    } catch (error: any) {
      console.error('Submission failed:', error);
      const msg = error?.data?.message || 'Failed to submit. Please try again.';
      showNotification(msg, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getAnsweredCount = () => {
    return Object.values(formValues).filter(v => v != null && v !== '').length;
  };

  const isCurrentPageComplete = () => {
    return getCurrentQuestions().every(q => {
      const val = formValues[q.id];
      return val !== undefined && val !== null && val !== '';
    });
  };

  const progress = healthQuestions.length > 0
    ? (getAnsweredCount() / healthQuestions.length) * 100
    : 0;

  return (
    <ThemeProvider theme={theme}>
      <Box sx={container}>
        <Paper elevation={24} sx={formPaper}>
          <Box sx={header}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
              <Heart size={32} fill="white" />
              <Box>
                <Typography variant="h4" fontWeight="700" sx={headerTitle}>
                  Health Assessment
                </Typography>
                <Typography variant="body2" sx={completeText}>
                  Answer all questions honestly for accurate results
                </Typography>
              </Box>
            </Stack>

            <Box sx={progressContainer}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="body2" fontWeight="600">
                  Progress: {getAnsweredCount()} of {healthQuestions.length}
                </Typography>
                <Typography variant="body2" fontWeight="600">
                  {Math.round(progress)}%
                </Typography>
              </Stack>
              <LinearProgress variant="determinate" value={progress} sx={progressBar} />
            </Box>
          </Box>

          <Box sx={questionIndicatorsContainer}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {healthQuestions.map((q, i) => (
                <Box
                  key={q.id}
                  sx={{
                    ...questionIndicator,
                    ...(i >= currentPage * questionsPerPage && i < (currentPage + 1) * questionsPerPage && {
                      backgroundColor: "#150c56",
                      color: "white",
                    }),
                    ...(formValues[q.id] && {
                      backgroundColor: "#6537ef",
                      color: "white",
                      borderColor: "#6537ef",
                    }),
                  }}
                >
                  {i + 1}
                </Box>
              ))}
            </Stack>
          </Box>

          <Box sx={{ padding: 4 }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing={3}>
                <Box sx={questionHeader}>
                  <Activity size={24} color="#6537ef" />
                  <Typography variant="h6" sx={questionTitle}>
                    {healthQuestions.length > 0
                      ? `Questions ${currentPage * questionsPerPage + 1} - ${Math.min((currentPage + 1) * questionsPerPage, healthQuestions.length)}`
                      : 'Loading questions...'}
                  </Typography>
                </Box>

                {isLoading ? (
                  <Box textAlign="center" py={4}>
                    <CircularProgress />
                    <Typography>Loading questions...</Typography>
                  </Box>
                ) : isError ? (
                  <Typography color="error" textAlign="center" py={4}>
                    Failed to load questions. Please try again later.
                  </Typography>
                ) : (
                  getCurrentQuestions().map((q, idx) => (
                    <Paper
                      key={q.id}
                      elevation={2}
                      sx={{
                        ...questionCard,
                        ...(formValues[q.id] ? questionCardActive : {}),
                      } as any} // FIXED: Bypass MUI sx type bug
                    >
                      <FormControl component="fieldset" fullWidth>
                        <FormLabel component="legend" sx={formLabel}>
                          {currentPage * questionsPerPage + idx + 1}. {q.question_text}
                          {q.is_required && ' *'}
                        </FormLabel>

                        {q.question_type === 'yes_no' && (
                          <Controller
                            name={q.id}
                            control={control}
                            rules={{ required: q.is_required ? 'Required' : false }}
                            render={({ field }) => (
                              <RadioGroup {...field} row sx={radioGroup}>
                                <FormControlLabel
                                  value="yes"
                                  control={<Radio sx={radioOption} />}
                                  label={
                                    <Chip
                                      label="Yes"
                                      sx={[
                                        chip,
                                        field.value === "yes" ? chipSelected : chipUnselected,
                                      ] as any} // FIXED: Bypass MUI sx type bug
                                    />
                                  }
                                />
                                <FormControlLabel
                                  value="no"
                                  control={<Radio sx={radioOption} />}
                                  label={
                                    <Chip
                                      label="No"
                                      sx={[
                                        chip,
                                        field.value === "no" ? chipSelected : chipUnselected,
                                      ] as any} // FIXED
                                    />
                                  }
                                />
                              </RadioGroup>
                            )}
                          />
                        )}

                        {q.question_type === 'number' && (
                          <Controller
                            name={q.id}
                            control={control}
                            rules={{
                              required: q.is_required ? 'Required' : false,
                              ...(q.min_value !== null && { min: { value: q.min_value, message: `Min ${q.min_value}` } }),
                              ...(q.max_value !== null && { max: { value: q.max_value, message: `Max ${q.max_value}` } }),
                            }}
                            render={({ field, fieldState: { error } }) => (
                              <TextField
                                {...field}
                                type="number"
                                fullWidth
                                size="small"
                                inputProps={{ min: q.min_value ?? undefined, max: q.max_value ?? undefined }}
                                error={!!error}
                                helperText={error?.message}
                              />
                            )}
                          />
                        )}

                        {(q.question_type === 'dropdown' || q.question_type === 'multiple_choice') && (
                          <Controller
                            name={q.id}
                            control={control}
                            rules={{ required: q.is_required ? 'Please select' : false }}
                            render={({ field, fieldState: { error } }) => (
                              <FormControl fullWidth error={!!error}>
                                <Select {...field} displayEmpty size="small">
                                  <MenuItem value="" disabled><em>Select an option</em></MenuItem>
                                  {parseOptions(q.options).map(opt => (
                                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                  ))}
                                </Select>
                                {error && <FormHelperText>{error.message}</FormHelperText>}
                              </FormControl>
                            )}
                          />
                        )}

                        {q.question_type === 'slider' && (
                          <Controller
                            name={q.id}
                            control={control}
                            render={({ field }) => (
                              <Box sx={{ px: 2, pt: 1 }}>
                                <Slider
                                  {...field}
                                  value={field.value ? Number(field.value) : q.min_value || 0}
                                  min={q.min_value || 0}
                                  max={q.max_value || 100}
                                  step={1}
                                  valueLabelDisplay="auto"
                                  marks={[
                                    { value: q.min_value || 0, label: `${q.min_value || 0}` },
                                    { value: q.max_value || 100, label: `${q.max_value || 100}` },
                                  ]}
                                />
                              </Box>
                            )}
                          />
                        )}

                        {q.question_type === 'text' && (
                          <Controller
                            name={q.id}
                            control={control}
                            rules={{ required: q.is_required ? 'Required' : false }}
                            render={({ field, fieldState: { error } }) => (
                              <TextField {...field} fullWidth size="small" error={!!error} helperText={error?.message} />
                            )}
                          />
                        )}

                        {q.question_type === 'textarea' && (
                          <Controller
                            name={q.id}
                            control={control}
                            rules={{ required: q.is_required ? 'Required' : false }}
                            render={({ field, fieldState: { error } }) => (
                              <TextField {...field} fullWidth multiline rows={3} size="small" error={!!error} helperText={error?.message} />
                            )}
                          />
                        )}
                      </FormControl>
                    </Paper>
                  ))
                )}
              </Stack>

              <Stack direction="row" spacing={2} sx={navigationButtons} justifyContent="space-between" mt={4}>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBack size={20} />}
                  onClick={handlePrevious}
                  disabled={currentPage === 0}
                  sx={navButton}
                >
                  Previous
                </Button>

                {currentPage < totalPages - 1 ? (
                  <Button
                    variant="contained"
                    color="secondary"
                    endIcon={<ArrowForward size={20} />}
                    onClick={handleNext}
                    disabled={!isCurrentPageComplete()}
                    sx={navButton}
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    endIcon={<Check size={20} />}
                    disabled={isSubmitting || getAnsweredCount() !== healthQuestions.length}
                    sx={submitButton}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Assessment'}
                  </Button>
                )}
              </Stack>
            </form>

            <Snackbar
              open={notification.open}
              autoHideDuration={6000}
              onClose={() => setNotification(prev => ({ ...prev, open: false }))}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <MuiAlert
                onClose={() => setNotification(prev => ({ ...prev, open: false }))}
                severity={notification.severity}
                elevation={6}
                variant="filled"
              >
                {notification.message}
              </MuiAlert>
            </Snackbar>
          </Box>
        </Paper>
      </Box>
    </ThemeProvider>
  );
}